import { useQuery } from '@tanstack/react-query';
import { adminListOrganizations } from '@/api/auth/admin/admin';
import type { AdminOrganizationResponse } from '@/api/auth/missionImpactAuthAPI.schemas';

/**
 * Server-side flag filters applied while materializing the organization set.
 * Leaving a flag undefined means "don't filter on it".
 */
export interface AdminOrganizationFilters {
  isPartner?: boolean;
  isNonProfit?: boolean;
}

/** Query key for the materialized admin organizations set. */
export const allAdminOrganizationsQueryKey = (filters: AdminOrganizationFilters) =>
  ['adminOrganizationsAll', filters] as const;

/**
 * Walks the cursor-paginated admin organizations change-feed
 * (`GET /v1/admin/organizations`) to completion and returns the full,
 * flag-filtered set in memory. This lets the Organizations list page perform
 * name search, alphabetical sort, and paging entirely client-side with no API
 * change, since the underlying endpoint is a sync change-feed (ordered by
 * UpdatedUtc) with no server-side name search.
 *
 * Suited to the current admin scale; if organization volume grows large enough
 * that the full walk is too heavy, a server-side search endpoint would be the
 * follow-up (see the Organization Administration UI spec).
 */
export function useAllAdminOrganizations(filters: AdminOrganizationFilters = {}) {
  return useQuery({
    queryKey: allAdminOrganizationsQueryKey(filters),
    queryFn: async ({ signal }): Promise<AdminOrganizationResponse[]> => {
      const all: AdminOrganizationResponse[] = [];
      let continuationToken: string | undefined;

      // Bounded loop guards against an unbounded walk if the feed misbehaves.
      for (let page = 0; page < 1000; page++) {
        const response = await adminListOrganizations(
          {
            pageSize: 100,
            continuationToken,
            isPartner: filters.isPartner,
            isNonProfit: filters.isNonProfit,
          },
          signal
        );

        const body = response.data;
        all.push(...(body.items ?? []));

        if (!body.hasMore || !body.continuationToken) {
          break;
        }
        continuationToken = body.continuationToken;
      }

      return all;
    },
  });
}
